import { BarChartOption, colors } from '@creditsights/styleguide-web';
import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import { ChangeChartDto, ChangeChartMetric } from '../../../../services/dtos/riskProducts/changeCharts.dto';
import { WidgetMetricDto } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import {
  calculateBarChartAxisLabelWidth,
  FONT_FAMILY,
  getBarChartBaseConfig,
  getTooltipMarkerHtml,
} from '../../../../utils/chartUtils';

const LEGEND_NEGATIVE_CHANGE = 'Negative Change';
const LEGEND_POSITIVE_CHANGE = 'Positive Change';
const chartGridMinHeight = 60;
const chartGridMaxHeight = 279;

export const getWidgetName = (metric?: ChangeChartMetric, metricOptions?: WidgetMetricDto[]): string => {
  const metricName = metricOptions?.find((option) => option.id === metric)?.name;
  return `Average Change: ${metricName}`;
};

export const isPointInGrid = (xPointInGrid: number, yPointInGrid: number, xRange: number[]): boolean => {
  return (
    xPointInGrid >= xRange[0] &&
    xPointInGrid <= xRange[1] &&
    yPointInGrid >= chartGridMinHeight &&
    yPointInGrid <= chartGridMaxHeight
  );
};
export const getChartConfig = (
  data: ChangeChartDto[] | undefined,
  metric: ChangeChartMetric | undefined,
  widgetWidth: number,
  metricOptions?: WidgetMetricDto[],
  forDownload = false
): BarChartOption | undefined => {
  if (!data || isEmpty(data) || !metric) {
    return undefined;
  }
  const baseConfig = getBarChartBaseConfig();

  return {
    ...baseConfig,
    grid: forDownload
      ? {
          ...baseConfig.grid,
          bottom: '25',
        }
      : baseConfig.grid,
    title: forDownload
      ? {
          text: getWidgetName(metric, metricOptions),
          textStyle: {
            fontSize: 18,
            lineHeight: 28,
            fontWeight: 700,
            color: colors.tertiary1000,
            fontFamily: FONT_FAMILY,
          },
          subtext: '{a|Source: CreditSights Analytics, LLC.}',
          subtextStyle: {
            rich: {
              a: {
                fontSize: 10,
                color: colors.tertiary400,
                verticalAlign: 'bottom',
                padding: [0, 0, -260, 5],
              },
            },
          },
        }
      : undefined,
    animation: !forDownload,
    tooltip: {
      ...baseConfig.tooltip,
      formatter: (series) => {
        const item = series.find((s) => s.value != null);
        if (!item) {
          return '';
        }
        const toolTipColor = item.value !== 0 ? getTooltipMarkerHtml(item.color) : '';
        return `
              ${toolTipColor}
              <span>
                ${item.name}: ${metric.startsWith('cqs') ? 'CQS' : 'CRE'} ${item.value}
              </span>
          `;
      },
    },
    xAxis: [
      {
        type: 'category',
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          overflow: 'truncate',
          width: calculateBarChartAxisLabelWidth(data.length, widgetWidth),
        },
        data: data.map((d) => d.key),
      },
    ],
    yAxis: [
      {
        axisLabel: {
          interval: 0,
          formatter: (value) => {
            return value < 0 ? `(${Math.abs(value)})` : value;
          },
        },
        type: 'value',
      },
    ],
    legend: {
      data: [LEGEND_NEGATIVE_CHANGE, LEGEND_POSITIVE_CHANGE],
      show: true,
      top: forDownload ? '10%' : undefined,
    },
    // @ts-ignore
    series: data.map((d, index) => ({
      type: 'bar',
      name: d[metric] >= 0 ? LEGEND_POSITIVE_CHANGE : LEGEND_NEGATIVE_CHANGE,
      stack: 'stack',
      data: new Array(data.length).fill(undefined).map((_, i) => {
        return i === index ? round(d[metric], 2) : undefined;
      }),
      emphasis: {
        focus: 'series',
      },
      itemStyle: {
        opacity: 0.8,
        color: d[metric] >= 0 ? colors['cat-low100'] : colors['cat-high100'],
      },
    })),
  };
};
